/*
 * File: OpsBoardV2.tsx
 * Path: src/features/ops-board-v2/OpsBoardV2.tsx
 * Description: Frozen-prototype shell transcription for the public AQUORIX Ops Board V2.
 * Author: Larry McLean + ChatGPT
 * Created: 2026-07-10
 * Version: 1.1.0
 *
 * Change Log:
 * - 2026-07-10: v1.0.0 - Add Phase B shell with live public operator data and no session rows.
 * - 2026-07-10: v1.1.0 - Add Phase C live session rows using the frozen prototype DOM and classes.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './OpsBoardV2.css';
import {
  OpsBoardSession,
  OpsBoardSummary,
  useOpsBoardV2Data
} from './useOpsBoardV2Data';

import { useOpsBoardPaging } from './useOpsBoardPaging';

function formatBoardDate(value: string, timezone: string) {
  const date = new Date(`${value}T12:00:00`);

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: timezone
  })
    .format(date)
    .replace(',', '')
    .replace(',', '')
    .toUpperCase();
}

function formatSyncTime(value: string, timezone: string) {
  const date = new Date(value);

  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone
  }).format(date);
}

function normalizeCondition(condition?: string | null) {
  return String(condition || 'SUNNY').trim().toUpperCase();
}

function SessionFlag({
  session
}: {
  session: OpsBoardSession;
}) {
  if (session.session_type === 'shore') {
    return (
      <svg
        className="flag"
        width="42"
        height="28"
        viewBox="0 0 42 28"
      >
        <rect
          width="42"
          height="28"
          rx="2"
          fill="#D8352A"
        />
        <path
          d="M3 3 L39 25"
          stroke="#fff"
          strokeWidth="6.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (
    session.session_type === 'boat' ||
    session.session_type === 'boat_charter'
  ) {
    return (
      <svg
        className="flag"
        width="46"
        height="30"
        viewBox="0 0 46 30"
      >
        <path
          d="M1 1 H45 L35 15 L45 29 H1 Z"
          fill="#fff"
          stroke="#9fb3bd"
          strokeWidth="1"
        />
        <path
          d="M18 1 H45 L35 15 L45 29 H18 Z"
          fill="#1F6FB2"
        />
      </svg>
    );
  }

  return null;
}

function SiteTitle({
  session,
  name
}: {
  session: OpsBoardSession;
  name: string;
}) {
  const words = String(name || '').trim().split(/\s+/);
  const last = words.length > 0 ? words[words.length - 1] : '';
  const head = words.slice(0, -1).join(' ');

  return (
    <span className="nm">
      {head ? head + ' ' : ''}
      <span className="tail">
        {last}
        <SessionFlag session={session} />
      </span>
    </span>
  );
}

function OpsBoardV2Row({
  session,
  summary
}: {
  session: OpsBoardSession;
  summary?: OpsBoardSummary;
}) {
  const meetTime =
    session.meet_time ||
    session.metadata?.meet_time ||
    null;

  const meetLocation =
    session.meet_location ||
    session.metadata?.meet_location ||
    session.metadata?.location ||
    null;

  const activityName =
    session.site_name ||
    session.title;

  const isCourse =
    session.type === 'course';

  const guideName =
    session.metadata?.lead_guide_name ||
    'Guide TBD';

  const instructorName =
    session.metadata?.instructor_name ||
    'Instructor TBD';

  const vesselName =
    session.metadata?.vessel_name ||
    'SHORE';

  const courseLocation =
    session.metadata?.location ||
    meetLocation ||
    'Location TBD';

  const nitroxCount =
    Number(summary?.nitrox_requested_count || 0);

  const gearCount =
    Number(summary?.rental_gear_count || 0);

  const waitlistCount =
    Number(summary?.waitlist_pax || 0);

  const remaining =
    Math.max(Number(session.capacity_remaining || 0), 0);

  const isFull =
    remaining === 0;

  const roster =
    Array.isArray(summary?.roster)
      ? summary?.roster || []
      : [];

  const isOpenSession = roster.length === 0;

  const isLow = remaining > 0 && remaining <= 2;

  const openWho = isCourse ? instructorName : guideName;

  const openWhere = meetLocation || 'Dive Center';

  return (
    <div className={isOpenSession ? 'row open' : 'row'}>
      <div className="tcol">
        <span className="time">{session.start_time}</span>
        <span className="type">
          {isCourse ? 'Course' : 'Dive'}
        </span>

        <div className="meet">
          {meetTime ? (
            <>
              Meet {meetTime}
              <br />
            </>
          ) : null}

          {meetLocation ? (
            <span className="place">{meetLocation}</span>
          ) : null}
        </div>
      </div>

      <div className="acol">
        <div className="site">
          <SiteTitle session={session} name={activityName} />
        </div>

        <div className="meta">
          {isOpenSession ? (
            <>
              {openWho} <span className="sep">·</span> {openWhere}
            </>
          ) : (
            <>
              {isCourse ? (
                <>
                  Instructor: {instructorName}
                  <br />
                  Location: {courseLocation}
                </>
              ) : (
                <>
                  Guide: {guideName}
                  <br />
                  Vessel: {vesselName}
                </>
              )}
              <br />
              EAN {nitroxCount} · Gear {gearCount}
            </>
          )}
        </div>
      </div>

      <div className="rcol">
        {roster.length > 0 ? (
          <div className="roster-grid">
            {roster.map((item, index) => {
              const name =
                typeof item === 'string'
                  ? item
                  : item.name;

              const nitroxRequested =
                typeof item === 'string'
                  ? false
                  : item.nitrox_requested === true;

              return (
                <span
                  className="diver"
                  key={session.id + '-diver-' + index}
                >
                  {name}
                  {nitroxRequested ? (
                    <span className="b ean">EAN</span>
                  ) : null}
                </span>
              );
            })}
          </div>
        ) : (
          <div className="soon">
            <span className="dots">&#9675; &#9675; &#9675;</span>
            Roster opens soon
          </div>
        )}
      </div>

      <div className="scol">
        <div className={isFull ? 'pillbox full' : isLow ? 'pillbox low' : 'pillbox'}>
          <div className="n">{remaining}</div>
        </div>

        <div className={isFull ? 'plabel full' : isLow ? 'plabel low' : 'plabel'}>
          {isFull ? 'FULL' : 'OPEN'}
        </div>

        {waitlistCount > 0 ? (
          <div className="waiting">
            {waitlistCount} WAITING
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OpsBoardV2() {
  const { operatorSlug } = useParams<{ operatorSlug?: string }>();
  const { data, error } = useOpsBoardV2Data(operatorSlug);
  const rowsRef = React.useRef<HTMLDivElement>(null);
  const measurementRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const {
    visibleSessions,
    measurementSessions,
    visibleDayTally
  } = useOpsBoardPaging({
    data,
    rowsRef,
    measurementRefs
  });

  if (error) {
    return <div className="page">AQUORIX OPS BOARD API ERROR: {error}</div>;
  }

  if (!data) {
    return <div className="page">LOADING AQUORIX OPS BOARD...</div>;
  }

  const boardDate = formatBoardDate(
    data.window.start,
    data.operator.timezone
  );

  const syncTime = formatSyncTime(
    data.generated_at,
    data.operator.timezone
  );

  const location = [
    data.operator.location_city,
    data.operator.location_region
  ]
    .filter(Boolean)
    .join(' · ');

  const condition = normalizeCondition(data.weather?.condition);
  const airTemperature = Math.round(data.weather?.air_temp_c ?? 0);
  const wind = Math.round(data.weather?.wind_kmh ?? 0);

  return (
    <div className="page">
      <div className="brandbar">
        <div className="logo">
          <img
            src={data.operator.logo_url || '/operator-logo.png'}
            alt={data.operator.name}
          />
        </div>

        <div className="brand">
          <h1>{data.operator.name.toUpperCase()}</h1>
          <div className="loc">{location}</div>
        </div>

        <div className="envwrap">
          <div className="vdiv"></div>

          <div className="env sun">
            <svg className="ico" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="5" fill="#F4B740" />
              <g
                stroke="#F4B740"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M19.4 4.6l-1.8 1.8M6.4 17.6l-1.8 1.8" />
              </g>
            </svg>

            <div className="lab">
              <div className="k">{condition}</div>
              <div className="s">
                Air {airTemperature}°C · Wind {wind}
              </div>
            </div>
          </div>

          <div className="vdiv"></div>

          <div className="env set">
            <svg className="ico" viewBox="0 0 24 24" fill="none">
              <path
                d="M2.5 19h19"
                stroke="#E0913C"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M6.5 19a5.5 5.5 0 0111 0"
                fill="#F4B740"
              />
              <g
                stroke="#E0913C"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M12 2.5v3M3.2 12H5M19 12h1.8M5.4 6.4l1.3 1.3M18.6 6.4l-1.3 1.3" />
              </g>
            </svg>

            <div className="lab">
              <div className="k">SUNSET</div>
              <div className="s">{data.sunset_time || '--:--'}</div>
            </div>
          </div>

          <div className="vdiv"></div>

          <div className="env date">
            <div className="d">
              {boardDate} &nbsp;·&nbsp; {syncTime}
            </div>
          </div>
        </div>
      </div>

      <div className="board">
        <div className="statusrow">
          <div className="online">
            <span className="dot"></span>
            Online · Last sync {syncTime}
          </div>

          <div className="tally">
            {visibleDayTally.totalPax} PAX
            <span className="sep">·</span>
            {visibleDayTally.pickupTotal} PU
            <span className="sep">·</span>
            {visibleDayTally.nitroxTotal} EAN
            <span className="sep">·</span>
            0 TX
            <span className="sep">·</span>
            {visibleDayTally.rentalGearTotal} GEAR
            <span className="sep">·</span>
            {visibleDayTally.waitlistPaxTotal} WAIT
          </div>
        </div>

        <div className="toprule"></div>

        <div className="rows" ref={rowsRef}>
          {measurementSessions.length === 0 ? (
            <div className="empty">
              <svg className="mark" viewBox="0 0 120 84" fill="none">
                <path d="M8 24 q 16 -15 32 0 t 32 0 t 32 0" stroke="#16B3C7" strokeWidth="4" strokeLinecap="round" />
                <path d="M8 44 q 16 -15 32 0 t 32 0 t 32 0" stroke="#15536E" strokeWidth="4" strokeLinecap="round" opacity=".82" />
                <path d="M8 64 q 16 -15 32 0 t 32 0 t 32 0" stroke="#6b8996" strokeWidth="4" strokeLinecap="round" opacity=".66" />
              </svg>
              <div className="head">Dive Operations</div>
              <div className="sub">The day at a glance: divers, departures, and readiness.</div>
            </div>
          ) : (
            visibleSessions.map((session) => (
              <OpsBoardV2Row
                key={session.id}
                session={session}
                summary={data.session_summaries[String(session.id)]}
              />
            ))
          )}
        </div>

        <div className="measure-stage" aria-hidden="true">
          <div className="rows">
            {measurementSessions.map((session) => (
              <div
                key={session.id + '-measure'}
                ref={(element) => {
                  measurementRefs.current[String(session.id)] = element;
                }}
              >
                <OpsBoardV2Row
                  session={session}
                  summary={data.session_summaries[String(session.id)]}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="legend">
          <div className="it">
            <svg
              className="fg"
              width="30"
              height="20"
              viewBox="0 0 30 20"
            >
              <rect
                width="30"
                height="20"
                rx="1.5"
                fill="#D8352A"
              />
              <path
                d="M2.5 2.5 L27.5 17.5"
                stroke="#fff"
                strokeWidth="4.5"
                strokeLinecap="round"
              />
            </svg>
            Shore dive
          </div>

          <div className="it">
            <svg
              className="fg"
              width="32"
              height="20"
              viewBox="0 0 32 20"
            >
              <path
                d="M0.5 0.5 H31 L24 10 L31 19.5 H0.5 Z"
                fill="#fff"
                stroke="#9fb3bd"
                strokeWidth=".8"
              />
              <path
                d="M13 0.5 H31 L24 10 L31 19.5 H13 Z"
                fill="#1F6FB2"
              />
            </svg>
            Boat dive
          </div>

          <div className="it">
            <span className="b ean">EAN</span>
            Nitrox
          </div>

          <div className="it">
            <span className="b tx">Tx</span>
            Trimix
          </div>

          <div className="it">
            <span className="sw low"></span>
            Two seats or fewer
          </div>

          <div className="it">
            <span className="sw gold"></span>
            Full
          </div>
        </div>
      </div>

      <div className="furniture">
        <div className="dan">
          <svg
            className="mark"
            width="56"
            height="42"
            viewBox="0 0 56 42"
          >
            <rect width="56" height="42" rx="6" fill="#DA291C" />
            <path
              d="M8 8 L48 34"
              stroke="#fff"
              strokeWidth="7"
              strokeLinecap="round"
            />
          </svg>

          <div className="txt">
            <div className="t">DAN EMERGENCY</div>
            <div className="p">+1 919 684 9111</div>
          </div>
        </div>

        <div className="note">
          Please remember to bring your passport for all boat trips.
          <br />
          You must leave a 24 hour surface interval after diving before
          flying.
        </div>

        <div className="reg">
          <div className="rt">
            <div className="t">SCAN TO REGISTER</div>
            <div className="s">Forms · Waivers</div>
          </div>

          <div className="qr">
            <QRCodeSVG
              value={data.operator.website || window.location.href}
              size={86}
              level="M"
              includeMargin={false}
            />
          </div>
        </div>
      </div>

      <div className="powered">
        Powered by <b>AQUORIX</b>
      </div>
    </div>
  );
}

export default OpsBoardV2;
